import { IProduct } from "src/interfaces/gtm-interfaces";
import { ReadonlyArray } from "src/domain/readonlyArray";

export const CURRENCY_PRECISION = 0.01;

export interface EcomProductArrayDiffResult {
  removed: EcomProductArray;
  added: EcomProductArray;
}

// If BigInt is available, we want to use it for numbers
declare const BigInt:
  | ((value: string | number | bigint | boolean) => bigint)
  | undefined;

function sumValue(products: readonly IProduct[]) {
  if (typeof BigInt === "function") {
    const bigintVal = products.reduce<bigint>((tot, item) => {
      const listPrice = BigInt(Math.round(item.price / CURRENCY_PRECISION));
      const discount = BigInt(
        Math.round((item.discount ?? 0) / CURRENCY_PRECISION),
      );
      return tot + (listPrice - discount) * BigInt(item.quantity ?? 1);
    }, BigInt(0));
    return Number(bigintVal) * CURRENCY_PRECISION;
  } else {
    return products.reduce<number>((tot, item) => {
      const listPrice = item.price * (item.quantity ?? 1);
      const discount = item.discount ?? 0;
      return tot + (listPrice - discount);
    }, 0);
  }
}

export class EcomProductArray extends ReadonlyArray.Typed<IProduct>() {
  public static diff(
    src: readonly IProduct[],
    dst: readonly IProduct[],
  ): EcomProductArrayDiffResult {
    return EcomProductArray.prototype.diffTo.call(src, dst);
  }

  /**
   * Calculate the total of an {@link IProduct} array
   * Use BigInt internally if available, using {@link CURRENCY_PRECISION}
   * for precision.
   *
   * @param products the array to sum up
   * @returns the total price of all items in the array (taking discounts into account)
   */
  public static calculateTotalFromProductArray(
    products: readonly IProduct[],
  ): number {
    if (products instanceof EcomProductArray) return products.totalValue;
    return sumValue(products);
  }

  public static productEqual(a: IProduct, b: IProduct) {
    const keys = new Set([
      ...(Object.keys(a) as (keyof IProduct)[]),
      ...(Object.keys(b) as (keyof IProduct)[]),
    ]);
    keys.delete("quantity");
    for (const key of keys) {
      // Loose equals for nullish values
      // undefined == null
      if (a[key] == null && b[key] == null) continue;
      if (a[key] !== b[key]) return false;
    }
    return true;
  }

  // We do not want .map() to produce a regular array,
  // not a EcomProductArray object
  static get [Symbol.species]() {
    return Array;
  }

  constructor();
  constructor(arrayLength: number | undefined);
  constructor(ProductArray: readonly IProduct[] | undefined);
  constructor(input?: number | readonly IProduct[]) {
    if (ReadonlyArray.isArray(input)) {
      // If the length !== 1, we can optimize using the Array constructor
      if (input.length !== 1) super(...input);
      else {
        // Otherwise the Array Constructor
        // can't see the difference between the length
        // or an Array with a singular element if that happens
        // to be numeric.
        // And since we need to use the super(...) call
        // we can't disambiguate with Array.of(...) either.
        super(1);
        [(this as { [k: number]: IProduct })[0]] = input;
      }
    } else if (typeof input === "number") {
      super(input);
    } else {
      super(0);
    }
  }

  /**
   * Get the total value of the EcomProductArray
   * Use BigInt internally if available, using {@link CURRENCY_PRECISION}
   * for precision.
   *
   * @returns the total price of all items in the list (taking discounts into account)
   */
  public get totalValue(): number {
    const value = sumValue(this);
    Object.defineProperty(this, "totalValue", {
      value,
      configurable: true,
      enumerable: false,
      writable: false,
    });
    return value;
  }

  public diffTo(
    this: readonly IProduct[],
    dst: readonly IProduct[],
  ): EcomProductArrayDiffResult {
    const srcBySKU = this.reduce<Record<string, IProduct>>((acc, item) => {
      acc[item.item_id] = item;
      return acc;
    }, {});
    const dstBySKU = dst.reduce<Record<string, IProduct>>((acc, item) => {
      acc[item.item_id] = item;
      return acc;
    }, {});

    const removed = new EcomProductArray(
      this.reduce<IProduct[]>((acc, item) => {
        if (dstBySKU[item.item_id] == null) acc.push(item);
        else if (!EcomProductArray.productEqual(dstBySKU[item.item_id], item)) {
          delete srcBySKU[item.item_id];
          acc.push(item);
        } else if (dstBySKU[item.item_id].quantity < item.quantity) {
          acc.push({
            ...item,
            quantity: item.quantity - dstBySKU[item.item_id].quantity,
          });
        }
        return acc;
      }, []),
    );

    const added = new EcomProductArray(
      dst.reduce<IProduct[]>((acc, item) => {
        if (srcBySKU[item.item_id] == null) acc.push(item);
        else if (srcBySKU[item.item_id].quantity < item.quantity) {
          acc.push({
            ...item,
            quantity: item.quantity - srcBySKU[item.item_id].quantity,
          });
        }
        return acc;
      }, []),
    );
    return {
      added,
      removed,
    };
  }
}
